import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  MessageSquare, Send, Loader, AlertCircle, Paperclip, X, FileText, Trash2,
} from 'lucide-react';
import apiClient from '../../utils/apiClient';
import { formatThreadStamp } from '../../utils/datetime';
import { useFacility } from '../../context/FacilityContext';

// Shared SMS conversation thread: chat-bubble rendering + polling + inline
// reply box. Used by BOTH the per-customer Text tab (TextTab) and the
// unified inbox (Communication -> Text). Keeping the bubble layout in one
// place means the two views stay in sync.
//
// Props:
//   customerId, facilityId  — the conversation to load + reply on
//   onActivity()            — optional; fired after a successful load and
//                             after a send so a parent (e.g. the inbox list)
//                             can refresh / clear an unread badge

// How often we re-fetch the thread so tenant replies appear without a
// manual refresh. Cleared on unmount.
const POLL_MS = 25_000;

// Attachment types the composer accepts: common images + documents. Mirrors
// the backend sms-media allowlist; the backend re-validates regardless.
const ATTACH_ACCEPT = '.jpg,.jpeg,.png,.gif,.webp,.heic,.pdf,.doc,.docx,image/*';
const MAX_ATTACHMENTS = 10;

// Best-effort image detection from a URL/filename extension so we know
// whether to render an inline thumbnail vs a file chip.
const IMAGE_EXTS = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.heic'];
const isImageUrl = (url) => {
  if (!url) return false;
  const clean = String(url).split('?')[0].toLowerCase();
  return IMAGE_EXTS.some((ext) => clean.endsWith(ext));
};

const fileLabel = (url) => {
  try {
    const path = String(url).split('?')[0];
    const tail = decodeURIComponent(path.substring(path.lastIndexOf('/') + 1));
    return tail || 'Attachment';
  } catch {
    return 'Attachment';
  }
};

// Render a single attachment URL inside a bubble: image -> clickable
// thumbnail (opens full in a new tab); other docs -> labeled chip link.
const AttachmentItem = ({ url, inbound }) => {
  if (isImageUrl(url)) {
    return (
      <a href={url} target="_blank" rel="noopener noreferrer" className="block">
        <img
          src={url}
          alt={fileLabel(url)}
          className="rounded-lg max-h-44 max-w-full object-cover border border-black/5"
        />
      </a>
    );
  }
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[12px] font-medium border ${
        inbound
          ? 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
          : 'bg-blue-500/20 text-white border-white/30 hover:bg-blue-500/30'
      }`}
    >
      <FileText className="h-3.5 w-3.5 flex-shrink-0" />
      <span className="truncate max-w-[180px]">{fileLabel(url)}</span>
    </a>
  );
};

// Bubble timestamps come from utils/datetime (formatThreadStamp) so the
// naive-UTC strings parse correctly and render in the facility's timezone.

const SourcePill = ({ source, label }) => {
  const map = {
    manual:     { cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', text: 'Manual' },
    bulk:       { cls: 'bg-blue-50 text-blue-700 border-blue-200',         text: 'Bulk' },
    automation: { cls: 'bg-slate-100 text-slate-700 border-slate-200',     text: 'Automation' },
  };
  const m = map[source] || { cls: 'bg-slate-100 text-slate-600 border-slate-200', text: label || source };
  return (
    <span className={`inline-flex px-1.5 py-0.5 rounded-full text-[9.5px] font-medium uppercase tracking-wider border ${m.cls}`}>
      {m.text}
    </span>
  );
};

const StatusTag = ({ status }) => {
  if (status === 'failed') {
    return (
      <span className="inline-flex px-1.5 py-0.5 rounded-full text-[9.5px] font-medium bg-red-50 text-red-700 border border-red-200">
        Failed
      </span>
    );
  }
  if (status === 'queued' || status === 'sending') {
    return (
      <span className="inline-flex px-1.5 py-0.5 rounded-full text-[9.5px] font-medium bg-amber-50 text-amber-700 border border-amber-200">
        Queued
      </span>
    );
  }
  // The carrier confirmed the handset received it (provider delivery
  // receipt). SMS has no read receipts, so "delivered" is the last word.
  if (status === 'delivered') {
    return (
      <span className="inline-flex px-1.5 py-0.5 rounded-full text-[9.5px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
        Delivered
      </span>
    );
  }
  return null;
};

const Bubble = ({ row, onDelete, timeZone }) => {
  const inbound = row.direction === 'inbound';
  const media = Array.isArray(row.media_urls) ? row.media_urls.filter(Boolean) : [];
  const text = row.body_full || row.body_preview || '';
  // A media-only message (MMS with no caption) shouldn't print "(no content)".
  const hasText = text.trim().length > 0;
  // Subtle hover-revealed delete, sitting on the OUTER edge of the bubble
  // (left of an outbound, right of an inbound) so it never covers content.
  const deleteBtn = (
    <button
      type="button"
      onClick={onDelete}
      aria-label="Delete this message"
      title="Delete message"
      className="flex-shrink-0 self-center inline-flex items-center justify-center h-5 w-5 rounded-full text-slate-300 opacity-0 group-hover:opacity-100 hover:bg-red-50 hover:text-red-500 transition-opacity focus:opacity-100"
    >
      <Trash2 className="h-3 w-3" />
    </button>
  );
  return (
    <div className={`group flex items-center gap-1 ${inbound ? 'justify-start' : 'justify-end'}`}>
      {!inbound && deleteBtn}
      <div className={`max-w-[78%] ${inbound ? 'items-start' : 'items-end'} flex flex-col gap-0.5`}>
        {(hasText || media.length === 0) && (
          <div
            className={
              inbound
                ? 'rounded-2xl rounded-bl-sm bg-slate-100 text-slate-800 px-3.5 py-2 text-[13px] whitespace-pre-wrap break-words'
                : 'rounded-2xl rounded-br-sm bg-blue-600 text-white px-3.5 py-2 text-[13px] whitespace-pre-wrap break-words'
            }
          >
            {hasText ? text : '(no content)'}
          </div>
        )}
        {media.length > 0 && (
          <div className={`flex flex-col gap-1.5 ${inbound ? 'items-start' : 'items-end'}`}>
            {media.map((url) => (
              <AttachmentItem key={url} url={url} inbound={inbound} />
            ))}
          </div>
        )}
        <div className={`flex items-center gap-1.5 px-1 ${inbound ? '' : 'flex-row-reverse'}`}>
          <span className="text-[10.5px] text-slate-400">{formatThreadStamp(row.sent_at, timeZone)}</span>
          {!inbound && row.source && row.source !== 'manual' && (
            <SourcePill source={row.source} label={row.source_label} />
          )}
          <StatusTag status={row.status} />
        </div>
      </div>
      {inbound && deleteBtn}
    </div>
  );
};

// Either `customerId` (a matched tenant thread) OR `phone` (an unmatched
// "Unknown number" thread) identifies the conversation. The three I/O paths
// — load, mark-read, send — branch on which one is present; everything else
// (bubbles, attachments, per-message delete) is identical.
const SmsThread = ({ customerId, phone, facilityId, onActivity }) => {
  // Render bubble timestamps in the facility's timezone (stored times are UTC).
  const { currentFacility } = useFacility();
  const timeZone = currentFacility?.timezone || undefined;
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);
  const [thread, setThread] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  // Staged attachments pending send: each is { url, filename, isImage }.
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);

  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);
  const onActivityRef = useRef(onActivity);
  onActivityRef.current = onActivity;

  // Opening a conversation marks its inbound texts read (idempotent on the
  // backend). Covers the per-customer Text tab; the inbox also calls
  // mark-read before mounting us, so a double-call is harmless.
  const markRead = useCallback(async () => {
    if (!facilityId) return;
    try {
      if (customerId) {
        await apiClient.post(
          `/api/facilities/${facilityId}/sms/conversations/${customerId}/mark-read`,
        );
      } else if (phone) {
        await apiClient.post(
          `/api/facilities/${facilityId}/sms/unmatched/mark-read`,
          null,
          { params: { phone } },
        );
      } else {
        return;
      }
      onActivityRef.current?.();
    } catch {
      // Non-fatal.
    }
  }, [facilityId, customerId, phone]);

  const loadThread = useCallback(async () => {
    if (!facilityId || (!customerId && !phone)) return;
    try {
      const url = customerId
        ? `/api/facilities/${facilityId}/customers/${customerId}/communications?channel=sms`
        : `/api/facilities/${facilityId}/sms/unmatched/thread?phone=${encodeURIComponent(phone)}`;
      const { data } = await apiClient.get(url);
      // Backend returns SMS oldest -> newest already; guard anyway.
      setThread(Array.isArray(data) ? data : []);
      setLoadError(null);
      onActivityRef.current?.();
    } catch (err) {
      setLoadError(err?.response?.data?.detail || 'Failed to load conversation.');
    } finally {
      setLoading(false);
    }
  }, [facilityId, customerId, phone]);

  // Delete one message by its customer_communications _id (the `id` the
  // communications endpoint returns on each row). Confirms first, then drops
  // it from the thread locally and refreshes so any parent badge/preview
  // re-syncs. Facility-scoped on the backend.
  const deleteMessage = useCallback(async (row) => {
    const messageId = row?.id || row?._id || row?.message_sid;
    if (!facilityId || !messageId) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm('Delete this message?')) return;
    // Optimistically remove it; refetch reconciles if the delete failed.
    setThread((prev) => prev.filter((r) => (r.id || r._id) !== (row.id || row._id)));
    try {
      await apiClient.delete(
        `/api/facilities/${facilityId}/sms/messages/${messageId}`,
      );
      onActivityRef.current?.();
    } catch (err) {
      setLoadError(err?.response?.data?.detail || 'Failed to delete message.');
    } finally {
      // Reconcile with the server regardless of outcome.
      loadThread();
    }
  }, [facilityId, loadThread]);

  // Initial load + polling so tenant replies show without a refresh.
  // Re-runs whenever the selected conversation changes.
  useEffect(() => {
    setLoading(true);
    setBody('');
    setSendError(null);
    setAttachments([]);
    loadThread();
    // Mark this conversation read on open so its unread badge clears.
    markRead();
    const id = setInterval(loadThread, POLL_MS);
    return () => clearInterval(id);
  }, [loadThread, markRead]);

  // Auto-scroll to the latest message whenever the thread grows.
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [thread]);

  // Upload each picked file to sms-media -> public URL, then stage it for
  // the next send. We upload immediately (not on send) so the operator sees
  // a preview and the send payload is just URLs.
  const handlePickFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    // Reset the input so picking the same file again re-fires onChange.
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (!files.length) return;
    const room = MAX_ATTACHMENTS - attachments.length;
    if (room <= 0) {
      setSendError(`You can attach up to ${MAX_ATTACHMENTS} files.`);
      return;
    }
    setSendError(null);
    setUploading(true);
    try {
      for (const file of files.slice(0, room)) {
        const form = new FormData();
        form.append('file', file);
        const { data } = await apiClient.post(
          `/api/facilities/${facilityId}/sms-media`,
          form,
          { headers: { 'Content-Type': 'multipart/form-data' } },
        );
        if (data?.media_url) {
          setAttachments((prev) => [
            ...prev,
            {
              url: data.media_url,
              filename: data.filename || file.name,
              isImage: isImageUrl(data.media_url) || (file.type || '').startsWith('image/'),
            },
          ]);
        }
      }
    } catch (err) {
      setSendError(err?.response?.data?.detail || 'Failed to upload attachment.');
    } finally {
      setUploading(false);
    }
  };

  const removeAttachment = (url) => {
    setAttachments((prev) => prev.filter((a) => a.url !== url));
  };

  const handleSend = async () => {
    const mediaUrls = attachments.map((a) => a.url);
    if (!body.trim() && mediaUrls.length === 0) {
      setSendError('Add a message or an attachment.');
      return;
    }
    setSendError(null);
    setSending(true);
    try {
      if (customerId) {
        await apiClient.post(
          `/api/facilities/${facilityId}/customers/${customerId}/send-sms`,
          { body, media_urls: mediaUrls },
        );
      } else {
        // Unmatched ("Unknown number") thread: reply straight to the number.
        await apiClient.post(
          `/api/facilities/${facilityId}/sms/send`,
          { to_phone: phone, body, media_urls: mediaUrls },
        );
      }
      setBody('');
      setAttachments([]);
      await loadThread();
    } catch (err) {
      setSendError(err?.response?.data?.detail || 'Failed to send SMS.');
    } finally {
      setSending(false);
    }
  };

  // 160 chars = single SMS segment. Above that, the message splits into
  // multi-part SMS (still bills per part). Counter goes amber at 160, red
  // past 320 so the operator notices before sending a 3-part message.
  const charCount = body.length;
  const counterClass =
    charCount > 320 ? 'text-red-600'
    : charCount > 160 ? 'text-amber-600'
    : 'text-slate-400';

  return (
    <div>
      {/* Conversation thread */}
      <div className="rounded-xl border border-slate-200/80 bg-white overflow-hidden mb-5">
        <div className="px-4 py-2.5 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4 text-blue-600" />
            <h3 className="text-[14px] font-semibold text-slate-900">Conversation</h3>
          </div>
          <span className="text-[12px] text-slate-400">
            {loading ? '...' : `${thread.length} message${thread.length === 1 ? '' : 's'}`}
          </span>
        </div>

        {loadError && (
          <div className="px-4 py-3 text-[13px] text-red-600">{loadError}</div>
        )}

        <div
          ref={scrollRef}
          className="px-4 py-4 space-y-3 max-h-[440px] overflow-y-auto bg-slate-50/40"
        >
          {loading ? (
            <div className="py-10 flex items-center justify-center">
              <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : thread.length === 0 ? (
            <div className="py-10 text-center text-[13px] text-slate-400">
              No messages yet. Start the conversation below.
            </div>
          ) : (
            thread.map((row) => (
              <Bubble
                key={row.id}
                row={row}
                timeZone={timeZone}
                onDelete={() => deleteMessage(row)}
              />
            ))
          )}
        </div>
      </div>

      {/* Compose */}
      <div className="rounded-xl border border-slate-200/80 bg-white p-4">
        <div className="mb-3">
          <label className="block text-[12px] font-medium text-slate-700 mb-1">
            Message
          </label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={3}
            placeholder="Type your message..."
            className="w-full border border-slate-300 rounded-md px-3 py-2 text-[13px] focus:outline-none focus:border-blue-500"
          />
          <div className={`mt-1 text-[11px] ${counterClass}`}>
            {charCount} / 160 char{charCount === 1 ? '' : 's'}
            {charCount > 160 && (
              <span> · message will split into multiple SMS</span>
            )}
          </div>
        </div>

        {/* Staged attachment previews — thumbnail for images, a labeled chip
            for docs. Each has a remove (X) button. */}
        {(attachments.length > 0 || uploading) && (
          <div className="mb-3 flex flex-wrap gap-2">
            {attachments.map((a) => (
              <div
                key={a.url}
                className="relative group inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 p-1 pr-2"
              >
                {a.isImage ? (
                  <img
                    src={a.url}
                    alt={a.filename}
                    className="h-10 w-10 rounded object-cover"
                  />
                ) : (
                  <span className="inline-flex items-center gap-1.5 px-1.5 text-[12px] text-slate-700">
                    <FileText className="h-3.5 w-3.5 text-slate-400" />
                  </span>
                )}
                <span className="text-[11.5px] text-slate-600 truncate max-w-[120px]">
                  {a.filename}
                </span>
                <button
                  type="button"
                  onClick={() => removeAttachment(a.url)}
                  aria-label={`Remove ${a.filename}`}
                  className="ml-0.5 inline-flex items-center justify-center h-4 w-4 rounded-full bg-slate-300 hover:bg-slate-400 text-white"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
            {uploading && (
              <div className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-2 text-[12px] text-slate-500">
                <Loader className="h-3.5 w-3.5 animate-spin" /> Uploading...
              </div>
            )}
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept={ATTACH_ACCEPT}
          multiple
          onChange={handlePickFiles}
          className="hidden"
        />

        <div className="flex items-center justify-between gap-3">
          {sendError ? (
            <div className="inline-flex items-center gap-1.5 text-[12px] text-red-600">
              <AlertCircle className="h-3.5 w-3.5" /> {sendError}
            </div>
          ) : <span />}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading || sending || attachments.length >= MAX_ATTACHMENTS}
              aria-label="Attach image or document"
              title="Attach image or document"
              className="inline-flex items-center justify-center h-9 w-9 rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50 transition-colors disabled:opacity-50"
            >
              <Paperclip className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={handleSend}
              disabled={sending || uploading}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-blue-600 text-white text-[13px] font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {sending ? (
                <><Loader className="h-3.5 w-3.5 animate-spin" /> Sending...</>
              ) : (
                <><Send className="h-3.5 w-3.5" /> Send</>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SmsThread;
